import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  CompanyDetailsEntity,
  CompanyEntity,
  CompanyLocationEntity,
  CustomerPersonEntity,
  PersonEntity,
  ScreeningCaseCompositeEntity,
  ScreeningCaseReviewStatusEntity,
} from '@nk/data-entities-lib';
import { CardComponent, CardContentComponent, HighlightTextPipe } from '@nk/ui-lib';
import { normalizeScreeningContext, ParsedRiskContext } from '../../utils/screening-case-review-context.utils';
import { ScreeningCardListDetailsInputData } from '../../utils/screening.utils';
import { PersonCompanyRolesComponent } from '../person-company-roles/person-company-roles.component';
import { PersonOwnershipComponent } from '../person-ownership/person-ownership.component';
import { ScreeningCaseReportPdfComponent } from '../screening-case-report-pdf/screening-case-report-pdf.component';
import { ScreeningCaseReviewEditorComponent } from '../screening-case-review-editor/screening-case-review-editor.component';
import { ScreeningCaseSupplierRiskContextComponent } from '../screening-case-supplier-risk-context/screening-case-supplier-risk-context.component';

interface EninEntityData {
  name: string | null;
  birthDate: Date | null;
  registrationDate: Date | null;
  countryName: string | null;
  address: string | null;
}

@Component({
  selector: 'nk-screening-case-comparison',
  imports: [
    DatePipe,
    TranslateModule,
    CardComponent,
    CardContentComponent,
    PersonCompanyRolesComponent,
    PersonOwnershipComponent,
    ScreeningCaseReportPdfComponent,
    ScreeningCaseReviewEditorComponent,
    ScreeningCaseSupplierRiskContextComponent,
    HighlightTextPipe,
  ],
  templateUrl: './screening-case-comparison.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningCaseComparisonComponent {
  // Required inputs
  public screeningCaseComposite = input.required<ScreeningCaseCompositeEntity>();

  // Optional entity inputs
  public person = input<PersonEntity | null>(null);
  public customerPerson = input<CustomerPersonEntity | null>(null);
  public company = input<CompanyEntity | null>(null);
  public companyDetails = input<CompanyDetailsEntity | null>(null);
  public companyLocations = input<CompanyLocationEntity[]>([]);

  // Action-related inputs
  public screeningConfigUuid = input<string | null>(null);
  public screeningCaseReviewStatusList = input<ScreeningCaseReviewStatusEntity[]>([]);
  public printView = input<boolean>(false);

  // Outputs
  public reviewSaved = output<void>();
  public reviewCreated = output<void>();

  public eninData = computed<EninEntityData>(() => {
    const person = this.person();
    const customerPerson = this.customerPerson();
    const company = this.company();
    const companyDetails = this.companyDetails();
    const companyLocations = this.companyLocations();

    if (person) {
      return {
        name: person.full_name,
        birthDate: person.birth_date,
        registrationDate: null,
        countryName: person.country_name,
        address: this._formatAddress(
          person.street_address,
          person.postal_code,
          person.postal_place,
          person.country_name,
        ),
      };
    }

    if (customerPerson) {
      return {
        name: customerPerson.full_name,
        birthDate: customerPerson.birth_date,
        registrationDate: null,
        countryName: null,
        address: null,
      };
    }

    if (company) {
      const primaryLocation = companyLocations.length > 0 ? companyLocations[0] : null;
      return {
        name: company.name,
        birthDate: null,
        registrationDate: companyDetails?.registration_date ?? null,
        countryName: primaryLocation?.country_name ?? null,
        address: primaryLocation
          ? this._formatAddress(
              primaryLocation.street_address,
              primaryLocation.postal_code,
              primaryLocation.postal_place,
              primaryLocation.country_name,
            )
          : null,
      };
    }

    return {
      name: null,
      birthDate: null,
      registrationDate: null,
      countryName: null,
      address: null,
    };
  });

  public supplierData = computed<ParsedRiskContext[]>(() => {
    const screeningContext = this.screeningCaseComposite()?.screening_case?.screening_context;
    return normalizeScreeningContext(screeningContext);
  });

  public highlightTexts = computed<string[]>(() => {
    const names = this.eninData().name?.toLowerCase().split(' ').filter(Boolean);
    return names ? names : [];
  });

  public isLegacy = computed(() => {
    const screeningContext = this.screeningCaseComposite()?.screening_case?.screening_context;
    return screeningContext === null || screeningContext === undefined;
  });

  public isCompany = computed(() => {
    const entityKey = this.screeningCaseComposite()?.screening_entity?.entity_key;
    return entityKey === 'company';
  });

  public personInputData = computed<ScreeningCardListDetailsInputData | null>(() => {
    const person = this.person();
    const customerPerson = this.customerPerson();

    if (person) {
      return {
        uuid: person.uuid,
        type: 'person',
        monitoring_origin_entity_company_uuid: null,
        person_master_uuid: person.person_master_uuid,
        birth_date: person.birth_date,
        birth_year: person.birth_year,
        national_identification_schema: person.national_identification_schema,
        street_address: person.street_address,
        postal_code: person.postal_code,
        postal_place: person.postal_place,
        municipality_name: person.municipality_name,
        country_name: person.country_name,
      };
    }

    if (customerPerson?.uuid) {
      return {
        uuid: customerPerson.uuid,
        type: 'customer_person',
        monitoring_origin_entity_company_uuid: null,
        person_master_uuid: null,
        birth_date: customerPerson.birth_date,
        birth_year: null,
        national_identification_schema: null,
        street_address: null,
        postal_code: null,
        postal_place: null,
        municipality_name: null,
        country_name: null,
      };
    }

    return null;
  });

  private _formatAddress(
    streetAddress: string | null,
    postalCode: string | null,
    postalPlace: string | null,
    countryName: string | null,
  ): string | null {
    const parts = [streetAddress, postalCode, postalPlace, countryName].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }
}
